<template>
  <Transition name="bottom-sheet">
    <div v-show="isOpen" class="fixed inset-0 z-[150] flex flex-col justify-end">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" @click="close"></div>
      
      <!-- Panel -->
      <div class="relative bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl p-6 max-h-[80vh] overflow-y-auto w-full max-w-lg mx-auto z-10 bottom-sheet-panel border-t border-gray-100 dark:border-gray-700"
           :style="{ paddingBottom: 'max(2rem, calc(env(safe-area-inset-bottom, 0px) + 1.5rem))' }">
        <!-- Handle for dragging feeling -->
        <div class="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-5"></div>
        
        <!-- Header -->
        <div class="flex items-start justify-between mb-6">
          <div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">Quick Actions</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Access your favorite tools</p>
          </div>
          <div class="flex items-center gap-2">
            <!-- Sort Trigger -->
            <button
              v-if="favoriteMenus.length > 0"
              @click="toggleSortMode"
              class="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              :class="[isSortMode ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/20' : '']"
              :title="isSortMode ? 'Done sorting' : 'Sort actions'"
            >
              <BarsArrowUpIcon v-slot="{}" v-if="!isSortMode" class="size-5" />
              <CheckIcon v-slot="{}" v-else class="size-5" />
            </button>
            <button @click="close" class="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
              <XMarkIcon class="size-5" />
            </button>
          </div>
        </div>
        
        <!-- Body (Grid of Favorites) -->
        <div v-if="favoriteMenus.length === 0" class="text-center py-12 text-gray-500 dark:text-gray-400">
          No favorite menus configured. Add some from the playlist menu.
        </div>
        <div v-else ref="menuContainerRef" class="grid grid-cols-4 gap-y-6 gap-x-4" :class="{ 'cursor-move': isSortMode }">
          <div v-for="menu in favoriteMenus" :key="menu.id" class="flex flex-col items-center text-center cursor-pointer" :class="[menu.id === 'zoom' ? 'col-span-2' : 'col-span-1']" @click="handleItemClick(menu.id)">
            <div class="flex items-center justify-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all duration-200 transform active:scale-95 shadow-sm border h-[56px]" :class="[menu.id === 'zoom' ? 'w-full max-w-[120px]' : 'w-[56px]', isSortMode ? 'border-dashed border-indigo-400 dark:border-indigo-500 animate-wiggle' : 'border-gray-100 dark:border-gray-700']">
              <component :is="menu.component" v-bind="menu.props || {}" v-on="menu.events || {}" />
            </div>
            <span class="text-[11px] text-gray-600 dark:text-gray-300 mt-2 font-medium break-words leading-tight w-full truncate px-0.5" :title="getMenuLabel(menu.id)">
              {{ getMenuLabel(menu.id) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, defineExpose, nextTick, onUnmounted } from 'vue';
import { useMenus, fetchFavoriteMenuIds, saveFavoriteMenuIds, menuList, listAccessMenusRestricted } from '@/components/common/menus/menus.js';
import { XMarkIcon, BarsArrowUpIcon, CheckIcon } from '@heroicons/vue/24/outline';
import Sortable from 'sortablejs';

const props = defineProps({
  allowAccess: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['event']);

const isOpen = ref(false);
const favoriteMenus = ref([]);
const favoriteMenuIds = ref([]);
const menuContainerRef = ref(null);
const isSortMode = ref(false);
let sortableInstance = null;

const { chordsMenu, scrollControlMenu, columnsMenu,
  downloadMenu, duplicateMenu, editSongMenu, playMenu,
  presentationMenu, notesMenu, transposeMenu,
  userNotesMenu, studioMenu, zoomInOutMenu, drawingMenu, shareMenu, stageSyncMenu, songMapMenu, chordDiagramsMenu,
  tunerMenu, metronomeMenu
} = useMenus(emit);

const allMenus = ref([
  chordsMenu,
  scrollControlMenu,
  columnsMenu,
  downloadMenu,
  duplicateMenu,
  editSongMenu,
  playMenu,
  presentationMenu,
  notesMenu,
  studioMenu,
  transposeMenu,
  userNotesMenu,
  zoomInOutMenu,
  drawingMenu,
  shareMenu,
  stageSyncMenu,
  songMapMenu,
  chordDiagramsMenu,
  tunerMenu,
  metronomeMenu
]);

const loadFavorites = () => {
  favoriteMenuIds.value = fetchFavoriteMenuIds();
  favoriteMenus.value = filterMenus();
};

const filterMenus = () => {
  let menusToShow = favoriteMenuIds.value
    .map(id => allMenus.value.find(menu => menu.id === id))
    .filter(menu => menu !== undefined);
  if (!props.allowAccess) {
    menusToShow = menusToShow.filter(menu => !listAccessMenusRestricted.includes(menu.id));
  }
  return menusToShow;
};

const getMenuLabel = (menuId) => {
  const item = menuList.find(m => m.id === menuId);
  return item ? item.label : '';
};

const open = () => {
  loadFavorites();
  isOpen.value = true;
};

const close = () => {
  isSortMode.value = false;
  destroySortable();
  isOpen.value = false;
};

const handleItemClick = (menuId) => {
  if (isSortMode.value) return; // Do nothing in sort mode!

  const keepOpenMenus = ['transpose', 'autoscroll'];
  if (!keepOpenMenus.includes(menuId)) {
    close();
  }
};

const toggleSortMode = () => {
  isSortMode.value = !isSortMode.value;
  if (isSortMode.value) {
    nextTick(() => {
      initSortable();
    });
  } else {
    destroySortable();
  }
};

const initSortable = () => {
  if (!menuContainerRef.value || sortableInstance) return;
  
  sortableInstance = Sortable.create(menuContainerRef.value, {
    animation: 150,
    ghostClass: 'opacity-50',
    chosenClass: 'cursor-grabbing',
    dragClass: 'cursor-grabbing',
    onEnd: (evt) => {
      const { oldIndex, newIndex } = evt;
      if (oldIndex !== newIndex && oldIndex !== undefined && newIndex !== undefined) {
        // Reorder the menus array
        const movedMenu = favoriteMenus.value.splice(oldIndex, 1)[0];
        favoriteMenus.value.splice(newIndex, 0, movedMenu);
        
        // Update favoriteMenuIds and save to localStorage
        favoriteMenuIds.value = favoriteMenus.value.map(menu => menu.id);
        saveFavoriteMenuIds(favoriteMenuIds.value);
      }
    }
  });
};

const destroySortable = () => {
  if (sortableInstance) {
    sortableInstance.destroy();
    sortableInstance = null;
  }
};

watch(() => props.allowAccess, () => {
  loadFavorites();
});

onUnmounted(() => {
  destroySortable();
});

defineExpose({ open, close });
</script>

<style scoped>
.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: opacity 0.25s ease;
}

.bottom-sheet-enter-active .bottom-sheet-panel,
.bottom-sheet-leave-active .bottom-sheet-panel {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.bottom-sheet-enter-from,
.bottom-sheet-leave-to {
  opacity: 0;
}

.bottom-sheet-enter-from .bottom-sheet-panel,
.bottom-sheet-leave-to .bottom-sheet-panel {
  transform: translateY(100%);
}

/* Deep selectors to normalize components of different sizes */
.bottom-sheet-panel :deep(.menu-list-item) {
  width: 40px !important;
  height: 40px !important;
  border-radius: 9999px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Make non-active items transparent */
.bottom-sheet-panel :deep(.menu-list-item:not(.bg-gradient-to-r)) {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

/* Ensure active items keep their blue/purple gradient and white icon color */
.bottom-sheet-panel :deep(.menu-list-item.bg-gradient-to-r) {
  background-image: linear-gradient(to right, #6366f1, #a855f7) !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

/* Normalize Transpose Button */
.bottom-sheet-panel :deep([data-transpose-button]) {
  width: 40px !important;
  height: 40px !important;
  border-radius: 9999px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.bottom-sheet-panel :deep([data-transpose-button] svg) {
  margin: 0 !important;
}

/* Normalize Auto Scroll Control Button */
.bottom-sheet-panel :deep(.relative > .flex.items-center) {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  gap: 0 !important;
}

.bottom-sheet-panel :deep([data-scroll-button]) {
  width: 24px !important;
  height: 40px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.bottom-sheet-panel :deep([data-scroll-button] svg) {
  margin: 0 !important;
}

@keyframes wiggle {
  0%, 100% { transform: rotate(-0.5deg); }
  50% { transform: rotate(0.5deg); }
}

.animate-wiggle {
  animation: wiggle 0.2s ease-in-out infinite;
}
</style>
